import { getState, getWidgetState } from "../state/modifications";
import { api_configuration } from "../../common";
import DestructorTree from "../DestructorTree";
import { ensure } from "../validation";
import escapeIdentifier from "./escapeIdentifier";
import { instruction_selection } from "./execute_instruction";
import { EventValue, ExpressionHandler, ExpressionValue, SortedKeys, TargetCallback } from "./ExpressionTypes";
import ImplementationVisitorContext, { WidgetConfigurationNode } from "./ImplementationVisitorContext";
import { visitScalar } from "./visit_scalar";
import api_modification = api_configuration.types.modifications.alan_api;
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import api_widget = api_configuration.libraries.gui_definition_lib.types.widget.alan_api;
import api_client_state = api_configuration.types.client_state.alan_api;
import context_selection = require("./context_selection");
import sort = require("../state/sorting");
import * as microtaskQueue from "../microtaskQueue";
import { iGraph } from "../state/Graph";

const expressionCache = new Map<api_widget.Cexpression, ExpressionHandler>();
export function visitExpression(expression: api_widget.Cexpression): ExpressionHandler {
	let cached_expression = expressionCache.get(expression);
	if (cached_expression === undefined) {
		cached_expression = internalVisitExpression(expression);
		expressionCache.set(expression, cached_expression);
	}
	return cached_expression;
}

function internalVisitExpression(expression: api_widget.Cexpression): ExpressionHandler {
	switch (expression.properties.type.state.name) {
		case "control": {
			const $ = expression.properties.type.state.node;
			return {
				execute: async () => $.properties.control_binding,
				subscribe: (implementation_context, callback, destructor) => {
					callback.add($.properties.control_binding, implementation_context, destructor, false);
				},
			};
		}
		case "create entry": {
			const $ = expression.properties.type.state.node;
			switch ($.properties.for_.state.name) {
				case "control node": {
					return {
						execute: async () => $.properties.node,
						subscribe: (implementation_context, callback, destructor) => {
							callback.add($.properties.node, implementation_context, destructor, false);
						},
					};
				}
				case "state": {
					const select_state = $.properties.for_.state.node;
					return {
						execute: async () => select_state,
						subscribe: (implementation_context, callback, destructor) => {
							callback.add(select_state, implementation_context, destructor, false);
						},
					};
				}
				default:
					return unexpectedState($.properties.for_.state);
			}
		}
		case "instruction": {
			const instruction = expression.properties.type.state.node;
			return {
				execute: async (implementation_context, destructor, event_value) => {
					await instruction_selection(
						instruction.properties.instruction,
						implementation_context,
						destructor,
						event_value
					);
					return undefined;
				},
				subscribe: () => {
					//no need to execute instructions.
				},
			};
		}
		case "match": {
			const match_type = expression.properties.type.state.node;
			const true_expression = visitOneOrMoreExpression(match_type.properties.cases.properties.true_);
			const false_expression = visitOneOrMoreExpression(match_type.properties.cases.properties.false_);
			switch (match_type.properties.type.state.name) {
				case "number": {
					const number_match = match_type.properties.type.state.node;
					const left_expression = visitScalar(number_match.properties.left_expression);
					const right_expression = visitScalar(number_match.properties.right_expression);

					return {
						execute: async (implementation_context, destructor, event_value) => {
							const left_value = await left_expression.execute(implementation_context, destructor, event_value);
							const right_value = await right_expression.execute(implementation_context, destructor, event_value);
							const values_equal = left_value !== undefined && left_value === right_value;
							if (values_equal) {
								return await true_expression.execute(implementation_context, destructor, event_value);
							} else {
								return await false_expression.execute(implementation_context, destructor, event_value);
							}
						},
						subscribe: (implementation_context, callback, destructor, event_value) => {
							let left_value: number | undefined = undefined;
							let right_value: number | undefined = undefined;
							let previous_result: boolean | undefined = undefined;
							let match_destructor: DestructorTree | undefined = undefined;

							const evaluate = () => {
								const values_equal = left_value !== undefined && left_value === right_value;
								if (previous_result === values_equal) {
									return;
								}
								previous_result = values_equal;
								match_destructor?.destroy();
								match_destructor = destructor.addChild();
								if (values_equal) {
									true_expression.subscribe(
										implementation_context,
										wrapWithDynamicArea(callback),
										match_destructor,
										event_value
									);
								} else {
									false_expression.subscribe(
										implementation_context,
										wrapWithDynamicArea(callback),
										match_destructor,
										event_value
									);
								}
							};

							left_expression.subscribe(
								implementation_context,
								{
									add: async (value: number) => {
										left_value = value;
										evaluate();
									},
								},
								destructor,
								event_value
							);
							right_expression.subscribe(
								implementation_context,
								{
									add: async (value: number) => {
										right_value = value;
										evaluate();
									},
								},
								destructor,
								event_value
							);
						},
					};
				}
				case "text": {
					const text_match = match_type.properties.type.state.node;
					const left_expression = visitScalar(text_match.properties.left_expression);
					const right_expression = visitScalar(text_match.properties.right_expression);

					return {
						execute: async (implementation_context, destructor, event_value) => {
							const left_value = await left_expression.execute(implementation_context, destructor, event_value);
							const right_value = await right_expression.execute(implementation_context, destructor, event_value);
							const values_equal = left_value !== undefined && left_value === right_value;
							if (values_equal) {
								return await true_expression.execute(implementation_context, destructor, event_value);
							} else {
								return await false_expression.execute(implementation_context, destructor, event_value);
							}
						},
						subscribe: (implementation_context, callback, destructor, event_value) => {
							let left_value: string | undefined = undefined;
							let right_value: string | undefined = undefined;
							let previous_result: boolean | undefined = undefined;
							let match_destructor: DestructorTree | undefined = undefined;

							const evaluate = () => {
								const values_equal = left_value !== undefined && left_value === right_value;
								if (previous_result === values_equal) {
									return;
								}
								previous_result = values_equal;
								match_destructor?.destroy();
								match_destructor = destructor.addChild();
								if (values_equal) {
									true_expression.subscribe(implementation_context, callback, match_destructor, event_value);
								} else {
									false_expression.subscribe(implementation_context, callback, match_destructor, event_value);
								}
							};

							left_expression.subscribe(
								implementation_context,
								{
									add: async (value: string) => {
										left_value = value;
										evaluate();
									},
								},
								destructor,
								event_value
							);
							right_expression.subscribe(
								implementation_context,
								{
									add: async (value: string) => {
										right_value = value;
										evaluate();
									},
								},
								destructor,
								event_value
							);
						},
					};
				}
				default:
					return unexpectedState(match_type.properties.type.state);
			}
		}
		case "node switch": {
			const node_switch = expression.properties.type.state.node;
			const node_expression = visitOneOrMoreExpression(node_switch.properties.cases.properties.node);
			const none_expression = visitOneOrMoreExpression(node_switch.properties.cases.properties.none);
			switch (node_switch.properties.from.state.name) {
				case "collection": {
					const from_collection = node_switch.properties.from.state.node;
					const escaped_collection_name = escapeIdentifier(from_collection.properties.collection.entry);
					return {
						execute: async (implementation_context, destructor, event_value) => {
							const collection_context = context_selection.context_selection(
								node_switch.properties.context,
								implementation_context,
								destructor
							);
							const collection = collection_context.current_binding_context.properties[
								escaped_collection_name
							] as api_client_state.AlanDictionary<any, any>;

							const key = (await visitScalar(from_collection.properties.key).execute(
								implementation_context,
								destructor,
								event_value
							)) as string;
							const collection_entry = collection.get(key);

							if (collection_entry !== undefined) {
								const entry_context = createEntryContext(
									collection_entry,
									collection_context,
									from_collection.properties.collection.entry,
									destructor
								);
								const variable_context = visitVariableAssignment(
									node_switch.properties.cases.properties.variable_assignment,
									entry_context,
									destructor
								);
								return await node_expression.execute(variable_context, destructor, event_value);
							} else {
								return await none_expression.execute(implementation_context, destructor, event_value);
							}
						},
						subscribe: (implementation_context, callback, destructor, event_value) => {
							const collection_context = context_selection.context_selection(
								node_switch.properties.context,
								implementation_context,
								destructor
							);
							const collection = collection_context.current_binding_context.properties[
								escaped_collection_name
							] as api_client_state.AlanDictionary<any, any>;
							let key: string | undefined = undefined;
							let entry_destructor: DestructorTree | undefined = undefined;
							const evaluate = () => {
								entry_destructor?.destroy();
								entry_destructor = destructor.addChild();
								const collection_entry = key === undefined ? undefined : collection.get(key);
								if (collection_entry !== undefined) {
									const entry_context = createEntryContext(
										collection_entry,
										collection_context,
										from_collection.properties.collection.entry,
										destructor
									);
									const variable_context = visitVariableAssignment(
										node_switch.properties.cases.properties.variable_assignment,
										entry_context,
										destructor
									);
									node_expression.subscribe(
										variable_context,
										wrapWithDynamicArea(callback),
										entry_destructor,
										event_value
									);
								} else {
									none_expression.subscribe(
										implementation_context,
										wrapWithDynamicArea(callback),
										entry_destructor,
										event_value
									);
								}
							};
							visitScalar(from_collection.properties.key).subscribe(
								implementation_context,
								{
									add: async (value: string) => {
										key = value;
										evaluate();
									},
								},
								destructor,
								event_value
							);

							destructor.subscribeToCollection(
								collection,
								{
									on_change: () => {
										evaluate();
									},
								},
								true
							);
						},
					};
				}
				case "list": {
					const from_list = node_switch.properties.from.state.node;
					return {
						execute: async (implementation_context, destructor, event_value) => {
							const result = await nodeSwitchListHandler(
								from_list,
								implementation_context,
								destructor,
								node_expression,
								none_expression,
								event_value
							).execute();
							return result;
						},
						subscribe: (implementation_context, callback, destructor, generate_key, event_value) => {
							nodeSwitchListHandler(
								from_list,
								implementation_context,
								destructor,
								node_expression,
								none_expression,
								event_value
							).subscribe(callback, generate_key!);
						},
					};
				}
				default:
					return unexpectedState(node_switch.properties.from.state);
			}
		}
		case "none": {
			return {
				execute: async () => undefined,
				subscribe: (implementation_context, callback, destructor) => {
					callback.add(undefined, implementation_context, destructor, false);
				},
			};
		}
		case "scalar": {
			const scalar = expression.properties.type.state.node;
			return visitScalar(scalar.properties.value);
		}
		case "sort collection": {
			const sort_collection = expression.properties.type.state.node;
			return {
				execute: async (implementation_context, destructor) => {
					const sort_context = context_selection.context_selection(
						sort_collection.properties.context,
						implementation_context,
						destructor
					);
					switch (sort_context.model_binding.properties.type.state.name) {
						case "ordered graph": {
							const ordered_graph = ensure(sort_context.current_binding_context, api_client_state.Cordered_graphs);
							const sorting = iGraph(ordered_graph).sort();
							switch (sort_collection.properties.direction.state.name) {
								case "ascending":
									return sorting;
								case "descending":
									return sorting.reverse();
							}
							break;
						}
						case "sort property":
							throw new Error("Not yet supported");
						case "query":
							if (
								sort_context.current_binding_context instanceof api_client_state.Ctext__type__columns ||
								sort_context.current_binding_context instanceof api_client_state.Cnumber__type__columns ||
								sort_context.current_binding_context instanceof api_client_state.Cstate_group__type__columns
							) {
								const query = sort_context.current_binding_context.parent.parent;
								switch (query.properties.has_result.state.name) {
									case "no":
										return undefined;
									case "yes":
										throw new Error("Not yet supported");
								}
							} else {
								throw new Error("Unexpected binding context for query sorting");
							}
							break;
						case "query number": {
							const number_column = ensure(
								sort_context.current_binding_context,
								api_client_state.Cnumber__type__columns
							);
							const query = number_column.parent.parent;
							switch (query.properties.has_result.state.name) {
								case "no":
									return undefined;
								case "yes":
									throw new Error("Not yet supported");
							}
							break;
						}
						case "query text": {
							const text_column = ensure(sort_context.current_binding_context, api_client_state.Ctext__type__columns);
							const query = text_column.parent.parent;
							switch (query.properties.has_result.state.name) {
								case "no":
									return undefined;
								case "yes":
									throw new Error("Not yet supported");
							}
							break;
						}
						case "query stategroup": {
							const stategroup_column = ensure(
								sort_context.current_binding_context,
								api_client_state.Cstate_group__type__columns
							);
							const query = stategroup_column.parent.parent;
							switch (query.properties.has_result.state.name) {
								case "no":
									return undefined;
								case "yes":
									throw new Error("Not yet supported");
							}
							break;
						}
						default:
							throw new Error("Unexpected model binding context for sorting");
					}
				},
				subscribe: (implementation_context, callback, destructor) => {
					const sort_context = context_selection.context_selection(
						sort_collection.properties.context,
						implementation_context,
						destructor
					);
					switch (sort_context.model_binding.properties.type.state.name) {
						case "ordered graph": {
							const ordered_graph = ensure(sort_context.current_binding_context, api_client_state.Cordered_graphs);
							const sorting = iGraph(ordered_graph).sorted;
							const subscription = sorting.subscribe({
								update: (new_sorting) => {
									const sort_copy = new_sorting.slice(0);
									switch (sort_collection.properties.direction.state.name) {
										case "ascending":
											break;
										case "descending":
											sort_copy.reverse();
											break;
									}
									callback.add(sort_copy, sort_context, destructor, false);
								},
							});
							destructor.onDestroy(() => sorting.unsubscribe(subscription));
							break;
						}
						case "sort property": {
							const collection = ensure(sort_context.current_binding_context, api_client_state.Ccollection);
							const sort_property = sort_context.model_binding.properties.type.state.node;
							sort.subscribeToSortCriteriaForProperty(
								collection,
								sort_property,
								destructor,
								sort_collection.properties.direction.state.name,
								(new_sorting) => {
									callback.add(new_sorting, sort_context, destructor, false);
								}
							);
							break;
						}
						case "query": {
							if (
								sort_context.current_binding_context instanceof api_client_state.Ctext__type__columns ||
								sort_context.current_binding_context instanceof api_client_state.Cnumber__type__columns ||
								sort_context.current_binding_context instanceof api_client_state.Cstate_group__type__columns
							) {
								sort.subscribToSortCriteriaForQueryProperty(
									sort_context.current_binding_context,
									destructor,
									sort_collection.properties.direction.state.name,
									(new_sorting) => {
										callback.add(new_sorting, sort_context, destructor, false);
									}
								);
							} else {
								throw new Error("Unexpected binding context for query sorting");
							}
							break;
						}
						case "query number": {
							const number_column = ensure(
								sort_context.current_binding_context,
								api_client_state.Cnumber__type__columns
							);
							sort.subscribToSortCriteriaForQueryProperty(
								number_column,
								destructor,
								sort_collection.properties.direction.state.name,
								(new_sorting) => {
									callback.add(new_sorting, sort_context, destructor, false);
								}
							);
							break;
						}
						case "query text": {
							const text_column = ensure(sort_context.current_binding_context, api_client_state.Ctext__type__columns);
							sort.subscribToSortCriteriaForQueryProperty(
								text_column,
								destructor,
								sort_collection.properties.direction.state.name,
								(new_sorting) => {
									callback.add(new_sorting, sort_context, destructor, false);
								}
							);
							break;
						}
						case "query stategroup": {
							const stategroup_column = ensure(
								sort_context.current_binding_context,
								api_client_state.Cstate_group__type__columns
							);
							sort.subscribToSortCriteriaForQueryProperty(
								stategroup_column,
								destructor,
								sort_collection.properties.direction.state.name,
								(new_sorting) => {
									callback.add(new_sorting, sort_context, destructor, false);
								}
							);
							break;
						}
						default:
							throw new Error("Unexpected model binding context for sorting");
					}
				},
			};
		}
		case "switch": {
			const state_switch = expression.properties.type.state.node;
			switch (state_switch.properties.type.state.name) {
				case "binding": {
					const binding_switch = state_switch.properties.type.state.node;
					return bindingSwitchHandler(binding_switch);
				}
				case "configuration": {
					const configuration_switch = state_switch.properties.type.state.node;
					return configurationSwitchHandler(configuration_switch);
				}
				default:
					return unexpectedState(state_switch.properties.type.state);
			}
		}
		case "walk": {
			const walk = expression.properties.type.state.node;
			switch (walk.properties.type.state.name) {
				case "configuration": {
					const configuration_walk = walk.properties.type.state.node;
					return {
						execute: async (implementation_context, destructor, event_value) => {
							const result = await walkConfigurationListHandler(
								configuration_walk,
								implementation_context,
								destructor,
								event_value
							).execute();
							return result;
						},
						subscribe: (implementation_context, callback, destructor, generate_key, event_value) => {
							walkConfigurationListHandler(
								configuration_walk,
								implementation_context,
								destructor,
								event_value
							).subscribe(callback, generate_key!);
						},
					};
				}
				case "widget binding": {
					const binding_walk = walk.properties.type.state.node;
					return walkCollectionBindingHandler(binding_walk);
				}
				default:
					return unexpectedState(walk.properties.type.state);
			}
		}
		default:
			return unexpectedState(expression.properties.type.state);
	}
}

function visitExpressionList(expression: api_widget.Cexpression_list): ExpressionHandler {
	let tail_expression: ExpressionHandler | undefined;
	switch (expression.properties.has_next.state.name) {
		case "no":
			break;
		case "yes": {
			const $ = expression.properties.has_next.state.node;
			tail_expression = visitExpressionList($.properties.next);
			break;
		}
		default:
			unexpectedState(expression.properties.has_next.state);
	}
	const expression_handler = visitExpression(expression.properties.expression);
	return {
		execute: async (implementation_context, destructor, event_value) => {
			let result: ExpressionValue[] = [];
			const variable_context = visitOptionalContextDefinition(
				expression.properties.optional_context_definition,
				implementation_context,
				destructor
			);
			const expression_result = await expression_handler.execute(variable_context, destructor, event_value);
			result = result.concat(expression_result);
			if (tail_expression !== undefined) {
				const tail_result = await tail_expression.execute(implementation_context, destructor, event_value);
				result = result.concat(tail_result);
			}

			return result;
		},
		subscribe: (implementation_context, c, destructor, generate_key, e) => {
			const entries: SortedKeys = [];
			entries[0] = [];
			const variable_context = visitOptionalContextDefinition(
				expression.properties.optional_context_definition,
				implementation_context,
				destructor
			);
			expression_handler.subscribe(
				variable_context,
				wrapWithKeyGenerator(c, 0, entries, generate_key!),
				destructor,
				generate_key,
				e
			);
			if (tail_expression !== undefined) {
				entries[1] = [];
				tail_expression?.subscribe(
					implementation_context,
					wrapWithKeyGenerator(c, 1, entries, generate_key!),
					destructor,
					generate_key,
					e
				);
			}
		},
	};
}

function wrapWithKeyGenerator(
	callback: TargetCallback,
	index: number,
	sorted_entries: SortedKeys,
	generate_key: () => string
): TargetCallback {
	let generated_key: string | undefined = undefined;

	return {
		add: (value, implementation_context, destructor, wrap, key) => {
			let new_key = key || generated_key;
			if (new_key === undefined) {
				new_key = generated_key = generate_key();
				sorted_entries[index] = generated_key;
				callback.update?.(sorted_entries);
			}
			callback.add(value, implementation_context, destructor, wrap, new_key);
		},
		update: (sorting) => {
			sorted_entries[index] = sorting;
			callback.update?.(sorted_entries);
		},
	};
}

function wrapWithDynamicArea(callback: TargetCallback): TargetCallback {
	return {
		add: (value, i, d, _w, key) => {
			callback.add(value, i, d, true, key);
		},
		update: callback.update,
	};
}

export function visitOneOrMoreExpression(expression: api_widget.Cone_or_more_expression): ExpressionHandler {
	switch (expression.properties.multiplicity.state.name) {
		case "plural": {
			const $ = expression.properties.multiplicity.state.node;
			return visitExpressionList($.properties.expression);
		}
		case "singular": {
			const $ = expression.properties.multiplicity.state.node;
			return visitExpression($.properties.expression);
		}
		default:
			unexpectedState(expression.properties.multiplicity.state);
	}
}

export function visitOptionalContextDefinition(
	definition: api_widget.Coptional_context_definition,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree
): ImplementationVisitorContext {
	switch (definition.properties.define_context.state.name) {
		case "no":
			return implementation_context;
		case "yes": {
			const $ = definition.properties.define_context.state.node;
			return visitVariable($.properties.variable, implementation_context, destructor);
		}
		default:
			unexpectedState(definition.properties.define_context.state);
	}
}

export function visitVariableAssignment(
	variable_assignment: api_widget.Cvariable_assignment,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree
): ImplementationVisitorContext {
	switch (variable_assignment.properties.assign.state.name) {
		case "no": {
			return implementation_context;
		}
		case "yes": {
			const $ = variable_assignment.properties.assign.state.node;
			const variable_context = visitVariable($.properties.variable, implementation_context, destructor);
			return variable_context.changeState({
				new_explicit_context: variable_context,
			});
		}
		default:
			unexpectedState(variable_assignment.properties.assign.state);
	}
}

export function visitVariable(
	variable: api_widget.Cvariable,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree
): ImplementationVisitorContext {
	const selected_context = context_selection.context_selection(
		variable.properties.context,
		implementation_context,
		destructor
	);
	const variable_context = selected_context.push_widget_implementation_frame();
	switch (variable.properties.more_variables.state.name) {
		case "no": {
			return variable_context;
		}
		case "yes": {
			const $ = variable.properties.more_variables.state.node;
			return visitVariable($.properties.variable, variable_context, destructor);
		}
		default:
			unexpectedState(variable.properties.more_variables.state);
	}
}

function walkConfigurationListHandler(
	walk_configuration_list: api_widget.Cconfiguration__type__walk,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree,
	event_value?: EventValue
) {
	const walk_implementation_context = context_selection.context_selection(
		walk_configuration_list.parent.properties.context,
		implementation_context,
		destructor
	);

	const gui_configuration = walk_implementation_context.gui_widget_configuration.properties.configuration
		.get(walk_configuration_list.properties.list.entry)!
		.properties.attribute.properties.type.cast("configuration");

	const widgets: api_views.Cgui_widget_configuration_node[] = [];

	switch (gui_configuration.properties.type.state.name) {
		case "list": {
			const bound_list = gui_configuration.properties.type.state.node;
			let widget_list = bound_list.properties.list;
			while (widget_list.properties.has_steps.state.name === "yes") {
				const $ = widget_list.properties.has_steps.state.node;
				widgets.push($.properties.configuration);
				widget_list = $.properties.tail;
			}
			break;
		}
		case "states list": {
			const states_list = gui_configuration.properties.type.state.node;
			const walkStates = (state: api_views.Cstates__states_list) => {
				widgets.push(state.properties.configuration);
				switch (state.properties.has_successor.state.name) {
					case "no":
						break;
					case "yes": {
						const $ = state.properties.has_successor.state.node;
						walkStates($.properties.successor.ref);
						break;
					}
					default:
						unexpectedState(state.properties.has_successor.state);
				}
			};
			switch (states_list.properties.has_states.state.name) {
				case "no":
					break;
				case "yes": {
					const $ = states_list.properties.has_states.state.node;
					walkStates($.properties.first.ref);
					break;
				}
				default:
					return unexpectedState(states_list.properties.has_states.state);
			}
			break;
		}
		case "number":
			throw new Error("number can not be bound to a configuration list entry");
		case "state group":
			throw new Error("state group can not be bound to a configuration list entry");
		case "text":
			break;
		default:
			return unexpectedState(gui_configuration.properties.type.state);
	}
	const entry_expression = visitOneOrMoreExpression(walk_configuration_list.parent.properties.entry_expression);
	return {
		execute: async () => {
			let results: ExpressionValue[] = [];
			for (const widget_entry of widgets) {
				const variable_context = visitVariable(
					walk_configuration_list.parent.properties.variable,
					walk_implementation_context.switchGuiWidgetConfiguration(widget_entry),
					destructor
				);
				const result = await entry_expression.execute(variable_context, destructor, event_value);
				results = results.concat(result);
			}
			return results;
		},
		subscribe: (callback: TargetCallback, generate_key: () => string) => {
			let index = 0;
			const entries: SortedKeys = [];
			for (const widget_entry of widgets) {
				entries[index] = [];
				const variable_context = visitVariable(
					walk_configuration_list.parent.properties.variable,
					walk_implementation_context.switchGuiWidgetConfiguration(widget_entry),
					destructor
				);
				entry_expression.subscribe(
					variable_context,
					wrapWithKeyGenerator(callback, index, entries, generate_key!),
					destructor,
					generate_key,
					event_value
				);
				index = index + 1;
			}
		},
	};
}

function nodeSwitchListHandler(
	from_list: api_widget.Clist__from,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree,
	node_expression: ExpressionHandler,
	none_expression: ExpressionHandler,
	event_value?: EventValue
) {
	const list_context = context_selection.context_selection(
		from_list.parent.properties.context,
		implementation_context,
		destructor
	);
	const list = list_context.gui_widget_configuration.properties.configuration
		.get(from_list.properties.list.entry)!
		.properties.attribute.properties.type.cast("configuration")
		.properties.type.cast("list");

	return {
		execute: async () => {
			const list_index = (await visitScalar(from_list.properties.index).execute(
				implementation_context,
				destructor,
				event_value
			)) as number | undefined;

			const list_entry = list_index === undefined ? undefined : getNodeAtIndex(list.properties.list, list_index);

			if (list_entry !== undefined) {
				const variable_context = visitVariableAssignment(
					from_list.parent.properties.cases.properties.variable_assignment,
					list_context.switchGuiWidgetConfiguration(list_entry),
					destructor
				);
				return await node_expression.execute(variable_context, destructor, event_value);
			} else {
				return await none_expression.execute(implementation_context, destructor, event_value);
			}
		},
		subscribe: (callback: TargetCallback, generate_key: () => string) => {
			let list_index: number | undefined;
			let list_destructor: DestructorTree | undefined;

			const evaluate = () => {
				list_destructor?.destroy();
				list_destructor = destructor.addChild();
				const list_entry = list_index === undefined ? undefined : getNodeAtIndex(list.properties.list, list_index);

				if (list_entry !== undefined) {
					const variable_context = visitVariableAssignment(
						from_list.parent.properties.cases.properties.variable_assignment,
						list_context.switchGuiWidgetConfiguration(list_entry),
						destructor
					);
					node_expression.subscribe(
						variable_context,
						wrapWithDynamicArea(callback),
						list_destructor,
						generate_key,
						event_value
					);
				} else {
					none_expression.subscribe(
						implementation_context,
						wrapWithDynamicArea(callback),
						list_destructor,
						generate_key,
						event_value
					);
				}
			};
			visitScalar(from_list.properties.index).subscribe(
				implementation_context,
				{
					add: async (value: number) => {
						if (list_index === undefined) {
							list_index = value;
							evaluate();
						} else if (list_index !== value) {
							list_index = value;
							evaluate();
						}
					},
				},
				destructor,
				generate_key,
				event_value
			);
		},
	};
}

function configurationSwitchHandler(configuration_switch: api_widget.Cconfiguration__type__switch): ExpressionHandler {
	const state_group_property = configuration_switch.properties.state_group.entry;
	const state_expressions: Record<string, ExpressionHandler> = {};
	for (const state of configuration_switch.properties.states.values()) {
		state_expressions[state.key.entry] = visitOneOrMoreExpression(state.properties.next);
	}
	return {
		execute: async (implementation_context, destructor, event_value) => {
			const switch_implementation_context = context_selection.context_selection(
				configuration_switch.parent.properties.context,
				implementation_context,
				destructor
			);

			const context_node = switch_implementation_context.view_context_node;
			const widget_configuration = switch_implementation_context.widget_configuration;

			const updateConfigurationState = async (state: string, configuration: WidgetConfigurationNode) => {
				const new_implementation_context = switch_implementation_context.changeState({
					new_widget_configuration: configuration,
				});
				const variable_context = visitVariableAssignment(
					configuration_switch.properties.states.get(state)!.properties.variable_assignment,
					new_implementation_context,
					destructor
				);
				const expression = state_expressions[state];
				return await expression.execute!(variable_context, destructor, event_value);
			};

			if (widget_configuration instanceof api_views.Cgui_widget_configuration_node) {
				const binding_context = widget_configuration.properties.configuration
					.get(state_group_property)
					?.properties.attribute.properties.type.cast("configuration")
					.properties.type.cast("state group");
				if (binding_context === undefined) {
					const widget_default_state =
						configuration_switch.properties.state_group.ref.properties.has_default.cast("yes");

					const state_value = getWidgetState(
						context_node,
						widget_configuration,
						widget_default_state.parent,
						switch_implementation_context.entry_keys
					);
					if (state_value.value === undefined) {
						return await updateConfigurationState(
							widget_default_state.properties.state.entry,
							widget_default_state.properties.state.ref.properties.node
						);
					} else {
						return await updateConfigurationState(
							state_value.value.properties.selected_state.entry,
							state_value.value.properties.configuration
						);
					}
				} else {
					const state_value = getState(context_node, binding_context, switch_implementation_context.entry_keys);
					if (state_value.value !== undefined) {
						return await updateConfigurationState(
							state_value.value.properties.selected_state.entry,
							state_value.value.properties.configuration
						);
					} else {
						return await updateConfigurationState(
							binding_context.properties.state.entry,
							binding_context.properties.configuration
						);
					}
				}
			} else if (widget_configuration instanceof api_modification.Cconfiguration_node) {
				const selected_state = widget_configuration.properties.attributes
					.get(state_group_property)!
					.properties.type.cast("state group");

				const binding_context = selected_state;
				return await updateConfigurationState(
					binding_context.properties.selected_state.entry,
					binding_context.properties.node
				);
			} else if (widget_configuration instanceof api_widget.Cwidget_configuration_node) {
				const widget_default_state = configuration_switch.properties.state_group.ref.properties.has_default.cast("yes");
				const state_value = getWidgetState(
					context_node,
					switch_implementation_context.last_gui_widget_configuration,
					widget_default_state.parent,
					switch_implementation_context.entry_keys
				);
				if (state_value.value === undefined) {
					return await updateConfigurationState(
						widget_default_state.properties.state.entry,
						widget_default_state.properties.state.ref.properties.node
					);
				} else {
					return await updateConfigurationState(
						state_value.value.properties.selected_state.entry,
						state_value.value.properties.configuration
					);
				}
			} else {
				unexpectedState(widget_configuration);
			}
		},
		subscribe: (implementation_context, callback, destructor, generate_key, event_value) => {
			const switch_implementation_context = context_selection.context_selection(
				configuration_switch.parent.properties.context,
				implementation_context,
				destructor
			);
			const context_node = switch_implementation_context.view_context_node;

			let stategroup_destructor: DestructorTree | undefined;
			const widget_configuration = switch_implementation_context.widget_configuration;

			const updateConfigurationState = (state: string, configuration: WidgetConfigurationNode) => {
				stategroup_destructor?.destroy();
				stategroup_destructor = destructor.addChild();
				const new_implementation_context = switch_implementation_context.changeState({
					new_widget_configuration: configuration,
				});
				const variable_context = visitVariableAssignment(
					configuration_switch.properties.states.get(state)!.properties.variable_assignment,
					new_implementation_context,
					destructor
				);

				const expression = state_expressions[state];
				expression.subscribe?.(
					variable_context,
					wrapWithDynamicArea(callback),
					stategroup_destructor,
					generate_key,
					event_value
				);
			};

			if (widget_configuration instanceof api_views.Cgui_widget_configuration_node) {
				const binding_context = widget_configuration.properties.configuration
					.get(state_group_property)
					?.properties.attribute.properties.type.cast("configuration")
					.properties.type.cast("state group");
				if (binding_context === undefined) {
					const widget_default_state =
						configuration_switch.properties.state_group.ref.properties.has_default.cast("yes");

					const state_value = getWidgetState(
						context_node,
						widget_configuration,
						widget_default_state.parent,
						switch_implementation_context.entry_keys
					);
					if (state_value.value === undefined) {
						updateConfigurationState(
							widget_default_state.properties.state.entry,
							widget_default_state.properties.state.ref.properties.node
						);
					} else {
						updateConfigurationState(
							state_value.value.properties.selected_state.entry,
							state_value.value.properties.configuration
						);
					}
					const subscription = state_value.subscribe((new_value) => {
						stategroup_destructor?.destroy();
						updateConfigurationState(new_value.properties.selected_state.entry, new_value.properties.configuration);
					});
					destructor.onDestroy(() => {
						subscription.unsubscribe();
					});
				} else {
					const state_value = getState(context_node, binding_context, switch_implementation_context.entry_keys);
					if (state_value.value !== undefined) {
						updateConfigurationState(
							state_value.value.properties.selected_state.entry,
							state_value.value.properties.configuration
						);
					} else {
						updateConfigurationState(binding_context.properties.state.entry, binding_context.properties.configuration);
					}
					const subscription = state_value.subscribe((new_state) => {
						stategroup_destructor?.destroy();
						updateConfigurationState(new_state.properties.selected_state.entry, new_state.properties.configuration);
					});
					destructor.onDestroy(() => {
						subscription.unsubscribe();
					});
				}
			} else if (widget_configuration instanceof api_modification.Cconfiguration_node) {
				const selected_state = widget_configuration.properties.attributes
					.get(state_group_property)!
					.properties.type.cast("state group");
				const binding_context = selected_state;
				const selected_state_subscription = binding_context.subscribe_selected_state({
					on_update: (new_state) => {
						stategroup_destructor?.destroy();
						updateConfigurationState(new_state, selected_state.properties.node);
					},
				});
				destructor.onDestroy(() => {
					selected_state_subscription.unsubscribe();
				});
			} else if (widget_configuration instanceof api_widget.Cwidget_configuration_node) {
				const widget_default_state = configuration_switch.properties.state_group.ref.properties.has_default.cast("yes");
				const state_value = getWidgetState(
					context_node,
					switch_implementation_context.last_gui_widget_configuration,
					widget_default_state.parent,
					switch_implementation_context.entry_keys
				);
				if (state_value.value === undefined) {
					updateConfigurationState(
						widget_default_state.properties.state.entry,
						widget_default_state.properties.state.ref.properties.node
					);
				} else {
					updateConfigurationState(
						state_value.value.properties.selected_state.entry,
						state_value.value.properties.configuration
					);
				}
				const subscription = state_value.subscribe((new_value) => {
					updateConfigurationState(new_value.properties.selected_state.entry, new_value.properties.configuration);
				});
				destructor.onDestroy(() => {
					subscription.unsubscribe();
				});
			} else {
				unexpectedState(widget_configuration);
			}
		},
	};
}

function walkCollectionBindingHandler(binding_walk: api_widget.Cwidget_binding): ExpressionHandler {
	const collection_name = binding_walk.properties.collection_property.entry;
	const escaped_collection_name = escapeIdentifier(collection_name);
	const entry_expression = visitOneOrMoreExpression(binding_walk.parent.properties.entry_expression);
	let sort_expression: ExpressionHandler | undefined = undefined;
	switch (binding_walk.properties.sort.state.name) {
		case "no":
			break;
		case "yes": {
			const sort = binding_walk.properties.sort.state.node;
			sort_expression = visitExpression(sort.properties.by);
			break;
		}
		default:
			unexpectedState(binding_walk.properties.sort.state);
	}
	return {
		execute: async (implementation_context, destructor, event_value) => {
			const collection_implementation_context = context_selection
				.context_selection(binding_walk.parent.properties.context, implementation_context, destructor)
				.switchClientStateBinderToProperty(collection_name, destructor);
			const collection = collection_implementation_context.current_binding_context.properties[
				escaped_collection_name
			] as api_client_state.AlanDictionary<any, any>;
			let result: ExpressionValue[] = [];
			for (const entry of collection.values()) {
				result = result.concat(
					await entry_expression.execute(
						visitVariable(
							binding_walk.parent.properties.variable,
							createEntryContext(entry, collection_implementation_context, collection_name, destructor),
							destructor
						),
						destructor,
						event_value
					)
				);
			}
			return result;
		},
		subscribe: (implementation_context, callback, destructor, generate_key, event_value) => {
			const collection_implementation_context = context_selection
				.context_selection(binding_walk.parent.properties.context, implementation_context, destructor)
				.switchClientStateBinderToProperty(collection_name, destructor);
			const collection = collection_implementation_context.current_binding_context.properties[
				escaped_collection_name
			] as api_client_state.AlanDictionary<any, any>;

			const sort_mapping: Record<string, SortedKeys> = {};
			const local_callback: TargetCallback = {
				add: callback.add,
				update: () => {
					callback.update!(sorted_entries);
				},
			};
			let sorted_entries: SortedKeys = [];
			let sorted = false; //Indicates if the entries are sorted by a sort definition.

			const added_entries = new Set<[api_client_state.AlanDictionaryEntry, DestructorTree]>();
			const evaluateAddedEntries = () => {
				for (const [entry, entry_destructor] of added_entries) {
					if (!entry_destructor.destroyed) {
						entry_expression.subscribe(
							visitVariable(
								binding_walk.parent.properties.variable,
								createEntryContext(entry, collection_implementation_context, collection_name, entry_destructor),
								destructor
							),
							wrapWithKeyGenerator(local_callback, 0, sort_mapping[entry.key_value], generate_key!),
							entry_destructor,
							generate_key,
							event_value
						);
					}
				}
				added_entries.clear();
			};

			destructor.subscribeToCollection(
				collection,
				{
					on_add: (entry, entry_destructor) => {
						sort_mapping[entry.key_value] = [];
						if (!sorted) {
							sorted_entries.push(sort_mapping[entry.key_value]);
						}
						added_entries.add([entry, entry_destructor]);
					},
					on_change: () => {
						microtaskQueue.add(evaluateAddedEntries);
					},
				},
				true
			);

			const applySorting = (sorting: string[]) => {
				sorted = true;
				sorted_entries = [];
				for (const key of sorting) {
					const entry_sorted_keys = sort_mapping[key];
					if (entry_sorted_keys !== undefined) {
						sorted_entries.push(entry_sorted_keys);
					}
				}
				local_callback.update?.(sorted_entries);
			};

			const sortOnTopology = () => {
				const sorting = sortEntriesOnTopology(collection_implementation_context, collection, collection_name);
				if (sorting !== undefined) {
					applySorting(sorting);
				} else {
					sortOnEntryOrder();
				}
			};

			const sortOnDefaultSorter = (sort_destructor: DestructorTree) => {
				const default_sorter = sort.subscribeToDefaultSorting(collection);
				if (default_sorter !== undefined) {
					default_sorter.subscribe(sort_destructor, applySorting);
				} else {
					sortOnTopology();
				}
			};

			const sortOnEntryOrder = () => {
				sorted = false;
				sorted_entries = [];
				for (const [key] of collection) {
					const entry_sorted_keys = sort_mapping[key];
					if (entry_sorted_keys !== undefined) {
						sorted_entries.push(entry_sorted_keys);
					}
				}
				local_callback.update?.(sorted_entries);
			};

			if (sort_expression !== undefined) {
				sort_expression.subscribe(
					collection_implementation_context,
					{
						add: async (sorted_keys: string[] | undefined, _i, sort_destructor) => {
							if (sorted_keys !== undefined) {
								applySorting(sorted_keys);
							} else {
								sortOnDefaultSorter(sort_destructor);
							}
						},
					},
					destructor,
					generate_key,
					event_value
				);
			} else {
				sortOnDefaultSorter(destructor);
			}
		},
	};
}

function bindingSwitchHandler(binding_switch: api_widget.Cbinding__type__switch): ExpressionHandler {
	const state_expressions: Record<string, ExpressionHandler> = {};
	const state_group_property = binding_switch.properties.property.entry;
	const escaped_state_group_property_name = escapeIdentifier(state_group_property);
	for (const state of binding_switch.properties.states.values()) {
		state_expressions[state.key.entry] = visitOneOrMoreExpression(state.properties.next);
	}
	return {
		execute: async (implementation_context, destructor, event_value) => {
			const context_selection_implementation_context = context_selection.context_selection(
				binding_switch.parent.properties.context,
				implementation_context,
				destructor
			);
			const new_visitor_context = context_selection_implementation_context.switchClientStateBinderToProperty(
				state_group_property,
				destructor
			);
			const binding_context = new_visitor_context.current_binding_context;
			const bound_property = binding_context.properties[
				escaped_state_group_property_name
			] as api_client_state.StateGroup<{ name: string; node: any; init: any }>;
			const state_group_binder = new_visitor_context.get_client_state_binder_AsStateGroupBinding(state_group_property);
			const state_implementation_context = new_visitor_context.changeState({
				new_binding_context: bound_property.state.node,
				new_client_state_binder: state_group_binder.states[bound_property.state.name].binding,
			});
			const variable_context = visitVariableAssignment(
				binding_switch.properties.states.get(bound_property.state.name)!.properties.variable_assignment,
				state_implementation_context,
				destructor
			);
			const expression = state_expressions[bound_property.state.name];
			return await expression.execute!(variable_context, destructor, event_value);
		},
		subscribe: (implementation_context, callback, destructor, generate_key, event_value) => {
			const context_selection_implementation_context = context_selection.context_selection(
				binding_switch.parent.properties.context,
				implementation_context,
				destructor
			);
			const new_visitor_context = context_selection_implementation_context.switchClientStateBinderToProperty(
				state_group_property,
				destructor
			);
			const binding_context = new_visitor_context.current_binding_context;
			const bound_property = binding_context.properties[
				escaped_state_group_property_name
			] as api_client_state.StateGroup<{ name: string; node: any; init: any }>;

			destructor.subscribeToStategroup(bound_property, (stategroup_destructor) => {
				const state_group_binder =
					new_visitor_context.get_client_state_binder_AsStateGroupBinding(state_group_property);
				const state_implementation_context = new_visitor_context.changeState({
					new_binding_context: bound_property.state.node,
					new_client_state_binder: state_group_binder.states[bound_property.state.name].binding,
				});
				const variable_context = visitVariableAssignment(
					binding_switch.properties.states.get(bound_property.state.name)!.properties.variable_assignment,
					state_implementation_context,
					destructor
				);

				const expression = state_expressions[bound_property.state.name];
				expression.subscribe?.(
					variable_context,
					{
						add: (value, i, d, _w, key) => {
							callback.add(value, i, d, true, key);
						},
						update: callback.update,
					},
					stategroup_destructor,
					generate_key,
					event_value
				);
			});
		},
	};
}

function createEntryContext(
	collection_entry: api_client_state.AlanDictionaryEntry,
	implementation_context: ImplementationVisitorContext,
	collection_name: string,
	destructor: DestructorTree
) {
	const key = collection_entry.key_value;
	let entry_implementation_context: ImplementationVisitorContext;
	const entry_context = implementation_context.get_client_state_binder_AsCollectionBinding(collection_name).entry;
	if (entry_context.onContext) {
		entry_implementation_context = entry_context
			.onContext(key, implementation_context, destructor)
			.switchClientStateBinder(entry_context.binding);
	} else {
		entry_implementation_context = implementation_context
			.switchBindingContext(collection_entry)
			.switchClientStateBinder(entry_context.binding);
	}
	const context = entry_implementation_context.changeState({
		entry_key: collection_name + ":" + key,
	});
	return context;
}

function getNodeAtIndex(
	list: api_views.Cgui_widget_configuration_list,
	index: number
): api_views.Cgui_widget_configuration_node | undefined {
	//NB index is 1 based
	return list.properties.has_steps.switch({
		no: undefined,
		yes: ($) => {
			if (index <= 1) {
				return $.properties.configuration;
			} else {
				return getNodeAtIndex($.properties.tail, index - 1);
			}
		},
	});
}

function sortEntriesOnTopology(
	collection_implementation_context: ImplementationVisitorContext,
	collection: api_client_state.AlanDictionary<any, any>,
	collection_name: string
): string[] | undefined {
	const order_constraint =
		collection_implementation_context.get_client_state_binder_AsCollectionBinding(collection_name).order_constraint;
	if (order_constraint !== undefined) {
		const sorting: string[] = [];
		const top = collection as api_client_state.AlanTopology<any, any, any>;
		top.topo_forEach(order_constraint, (entry: api_client_state.AlanDictionaryEntry) => {
			sorting!.push(entry.key_value);
		});
		return sorting;
	} else {
		return undefined;
	}
}
